import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Toaster } from "sonner";
import DashboardPage from "@/pages/Dashboard";
import Stages from "@/pages/Stages/Stages";
import Login from "@/pages/Login";
import SetPassword from "@/pages/SetPassword";
import { ReactNode } from "react";
import MainLayout from "@/components/layout/MainLayout";
import Calender from "@/pages/Calender";
import PipelineView from "@/pages/Pipeline";
import SearchResultsPage from "./components/layout/SearchResults";
import SettingsPage from "./pages/Settings";
import LeadsPage from "./pages/Stages/Leads";
import ProjectsPage from "./pages/Projects";
import UsersPage from "./pages/Users";
import AuditLogs from "./pages/AuditLogs";
import ClientsPage from "./pages/Clients";
import InvoicesPage from "./pages/Invoices";
import { isLoggedIn as checkIsLoggedIn } from "@/utils/service";


const ActiveProjectsPage = () => (
  <div className="p-8">
    <h1 className="text-2xl font-bold">Active Projects</h1>
  </div>
);
const CompletedProjectsPage = () => (
  <div className="p-8">
    <h1 className="text-2xl font-bold">Completed Projects</h1>
  </div>
);
const NewProjectPage = () => (
  <div className="p-8">
    <h1 className="text-2xl font-bold">Create New Project</h1>
  </div>
);

const GroupsPage = () => (
  <div className="p-8">
    <h1 className="text-2xl font-bold">Groups</h1>
  </div>
);
const MessagesPage = () => (
  <div className="p-8">
    <h1 className="text-2xl font-bold">Messages</h1>
  </div>
);
const TasksPage = () => (
  <div className="p-8">
    <h1 className="text-2xl font-bold">Tasks</h1>
  </div>
);

const PerformancePage = () => (
  <div className="p-8">
    <h1 className="text-2xl font-bold">Performance Analytics</h1>
  </div>
);
const RevenuePage = () => (
  <div className="p-8">
    <h1 className="text-2xl font-bold">Revenue Analytics</h1>
  </div>
);
const ReportsPage = () => (
  <div className="p-8">
    <h1 className="text-2xl font-bold">Reports</h1>
  </div>
);

const NotificationsPage = () => (
  <div className="p-8">
    <h1 className="text-2xl font-bold">Notifications</h1>
  </div>
);

const ProtectedRoute = ({ children }: { children: ReactNode }) => {
  if (!checkIsLoggedIn()) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

const PublicRoute = ({ children }: { children: ReactNode }) => {
  if (checkIsLoggedIn()) {
    return <Navigate to="/" replace />;
  }
  return children;
};

// Wrapper component for pages that need MainLayout
const LayoutWrapper = ({ children }: { children: ReactNode }) => {
  return <MainLayout>{children}</MainLayout>;
};

function AppContent() {
  return (
    <Router>
      <div className="min-h-screen bg-background">
        <Routes>
          {/* Public Routes - No Layout */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          <Route
            path="/set-password"
            element={
              <PublicRoute>
                <SetPassword />
              </PublicRoute>
            }
          />

          {/* Protected Routes - All wrapped in MainLayout */}
          {/* Dashboard */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <DashboardPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/search"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <SearchResultsPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />

          {/* Workflow Routes */}
          <Route
            path="/workflow/all-stages"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <Stages />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />

<Route
            path="/sales/leads"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <LeadsPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />

          {/* Projects Routes */}
          <Route
            path="/projects/all"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <ProjectsPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/active"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <ActiveProjectsPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/completed"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <CompletedProjectsPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/new"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <NewProjectPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />

          {/* Pipeline Routes */}
          <Route
            path="/pipeline/view"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <PipelineView />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />

<Route
            path="/users"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <UsersPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />

          {/* Clients Routes */}
          <Route
            path="/clients"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <ClientsPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/clients/new"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <ClientsPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />

          {/* Invoices Route */}
          <Route
            path="/invoices"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <InvoicesPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />

          {/* Team Routes */}
          <Route
            path="/team/groups"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <GroupsPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/team/messages"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <MessagesPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/team/tasks"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <TasksPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />

          {/* Analytics Routes */}
          <Route
            path="/analytics/performance"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <PerformancePage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics/revenue"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <RevenuePage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics/reports"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <ReportsPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />

          {/* Other Routes */}
          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <NotificationsPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/calendar"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <Calender />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/audit-logs"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <AuditLogs />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <SettingsPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="*"
            element={
              checkIsLoggedIn() ? (
                <Navigate to="/" replace />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
        </Routes>
        <Toaster position="bottom-right" closeButton richColors={false} />
      </div>
    </Router>
  );
}

function App() {
  return <AppContent />;
}

export default App;